import { useState } from "react";
import { NAV_LINKS, SOCIAL_LINKS } from "../../data/siteData";
import { useCart } from "../../context/CartContext";
import { useWishlist } from "../../context/WishlistContext";
import logo from "../../assets/images/logo.jpeg";
import "./Navbar.css";

function Navbar({ currentPath, onNavigate }) {
  const { count: cartCount } = useCart();
  const { count: wishlistCount } = useWishlist();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLinkClick = (e, path) => {
    e.preventDefault();

    if (onNavigate) {
      onNavigate(path);
    }

    setIsMobileMenuOpen(false);
  };

  const handleWishlistClick = () => {
    if (onNavigate) {
      onNavigate("/wishlist");
    }

    setIsMobileMenuOpen(false);
  };

  const handleCartClick = () => {
    if (onNavigate) {
      onNavigate("/cart");
    }

    setIsMobileMenuOpen(false);
  };

  return (
    <header className="navbar">

      {/* =====================================================
          DESKTOP / WEB LOGO
      ====================================================== */}
      <div className="navbar__logo-wrapper">
        <a
          href="/"
          onClick={(e) => handleLinkClick(e, "/")}
          className="navbar__logo-link"
        >
          <img
            src={logo}
            alt="Your Care"
            className="navbar__logo"
          />

          <span className="navbar__logo-text">
            <span className="navbar__logo-title">YOUR CARE</span>
            <span className="navbar__logo-tagline">Care that Clicks</span>
          </span>
        </a>
      </div>


      {/* =====================================================
          MOBILE HEADER
      ====================================================== */}
      <div className="navbar__mobile-header">

        {/* Mobile Menu Button */}
        <button
          type="button"
          className={`navbar__menu-button ${
            isMobileMenuOpen ? "is-open" : ""
          }`}
          onClick={() =>
            setIsMobileMenuOpen((prev) => !prev)
          }
          aria-label={
            isMobileMenuOpen
              ? "Close menu"
              : "Open menu"
          }
        >
          {isMobileMenuOpen ? (
            <svg
              width="27"
              height="27"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <line x1="5" y1="5" x2="19" y2="19" />
              <line x1="19" y1="5" x2="5" y2="19" />
            </svg>
          ) : (
            <svg
              width="27"
              height="27"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <line x1="4" y1="7" x2="20" y2="7" />
              <line x1="4" y1="12" x2="20" y2="12" />
              <line x1="4" y1="17" x2="20" y2="17" />
            </svg>
          )}
        </button>


        {/* Mobile Center Logo */}
        <a
          href="/"
          onClick={(e) => handleLinkClick(e, "/")}
          className="navbar__mobile-logo-link"
        >
          <img
            src={logo}
            alt="Your Care"
            className="navbar__mobile-logo"
          />

          <span className="navbar__mobile-logo-text">
            <span className="navbar__mobile-logo-title">YOUR CARE</span>
            <span className="navbar__mobile-logo-tagline">Care that Clicks</span>
          </span>
        </a>


        {/* Mobile Wishlist + Cart */}
        <div className="navbar__mobile-actions">

          {/* Wishlist */}
          <div
            className="navbar__mobile-icon-box"
            onClick={handleWishlistClick}
            title="Wishlist"
          >
            <svg
              width="29"
              height="29"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M12 21s-7.5-4.6-10-9.1C.5 8.4 2.3 5 5.8 5c2 0 3.4 1 4.4 2.4C11.3 6 12.7 5 14.7 5c3.5 0 5.3 3.4 3.8 6.9C19.5 16.4 12 21 12 21Z" />
            </svg>

            <span className="navbar__badge">
              {wishlistCount}
            </span>
          </div>


          {/* Cart */}
          <div
            className="navbar__mobile-icon-box"
            onClick={handleCartClick}
            title="Cart"
          >
            <svg
              width="29"
              height="29"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
            >
              <circle cx="9" cy="21" r="1" />
              <circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
            </svg>

            <span className="navbar__badge">
              {cartCount}
            </span>
          </div>

        </div>
      </div>


      {/* =====================================================
          DESKTOP BOTTOM ROW
      ====================================================== */}
      <div className="navbar__bottom">

        {/* ===================================================
            LEFT SOCIAL ICONS
        ==================================================== */}
        <div className="navbar__socials">

          <a
            href={SOCIAL_LINKS.facebook}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
          >
            <svg
              width="28"
              height="28"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
            </svg>
          </a>


          <a
            href={SOCIAL_LINKS.instagram}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
          >
            <svg
              width="28"
              height="28"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
            >
              <rect
                x="2"
                y="2"
                width="20"
                height="20"
                rx="5"
                ry="5"
              />

              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />

              <line
                x1="17.5"
                y1="6.5"
                x2="17.51"
                y2="6.5"
              />
            </svg>
          </a>


          <a
            href={SOCIAL_LINKS.tiktok}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="TikTok"
          >
            <svg
              width="28"
              height="28"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
            </svg>
          </a>

        </div>


        {/* ===================================================
            DESKTOP NAVIGATION
        ==================================================== */}
        <nav className="navbar__nav">

          {NAV_LINKS.map((link) => (
            <a
              key={link.path}
              href={link.path}
              className={`navbar__link ${
                currentPath === link.path
                  ? "active"
                  : ""
              }`}
              onClick={(e) =>
                handleLinkClick(e, link.path)
              }
            >
              {link.label}
            </a>
          ))}

        </nav>


        {/* ===================================================
            DESKTOP RIGHT ACTIONS
        ==================================================== */}
        <div className="navbar__actions">

          {/* Wishlist */}
          <div
            className="navbar__icon-box"
            onClick={handleWishlistClick}
            title="Wishlist"
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M12 21s-7.5-4.6-10-9.1C.5 8.4 2.3 5 5.8 5c2 0 3.4 1 4.4 2.4C11.3 6 12.7 5 14.7 5c3.5 0 5.3 3.4 3.8 6.9C19.5 16.4 12 21 12 21Z" />
            </svg>

            <span className="navbar__badge">
              {wishlistCount}
            </span>
          </div>


          {/* Cart */}
          <div
            className="navbar__icon-box"
            onClick={handleCartClick}
            title="Cart"
          >
            <svg
              width="32"
              height="32"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
            >
              <circle cx="9" cy="21" r="1" />
              <circle cx="20" cy="21" r="1" />

              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
            </svg>

            <span className="navbar__badge">
              {cartCount}
            </span>
          </div>

        </div>

      </div>


      {/* =====================================================
          MOBILE MENU
      ====================================================== */}
      <div
        className={`navbar__mobile-menu ${
          isMobileMenuOpen ? "show" : ""
        }`}
      >

        <nav className="navbar__mobile-nav">

          {NAV_LINKS.map((link) => (
            <a
              key={link.path}
              href={link.path}
              className={`navbar__mobile-link ${
                currentPath === link.path
                  ? "active"
                  : ""
              }`}
              onClick={(e) =>
                handleLinkClick(e, link.path)
              }
            >
              {link.label}
            </a>
          ))}

        </nav>


        {/* Mobile Divider */}
        <div className="navbar__mobile-divider"></div>


        {/* Mobile Social Icons */}
        <div className="navbar__mobile-socials">

          <a
            href={SOCIAL_LINKS.facebook}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
          >
            <svg
              width="25"
              height="25"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
            </svg>
          </a>


          <a
            href={SOCIAL_LINKS.instagram}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
          >
            <svg
              width="25"
              height="25"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
            >
              <rect
                x="2"
                y="2"
                width="20"
                height="20"
                rx="5"
                ry="5"
              />

              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />

              <line
                x1="17.5"
                y1="6.5"
                x2="17.51"
                y2="6.5"
              />
            </svg>
          </a>


          <a
            href={SOCIAL_LINKS.tiktok}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="TikTok"
          >
            <svg
              width="25"
              height="25"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
            </svg>
          </a>

        </div>

      </div>

    </header>
  );
}

export default Navbar;